import { useEffect, useState } from 'react'
import {
  api,
  type AgreementHeadline,
  type EvaluationDetail,
  type Standing,
  type VerdictReport,
  type VerdictReportFund,
} from '../api'
import { agreementText } from '../agreementWords'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'
import { Explain } from '../components/Explain'
import { FilterHeader, Showing, SortHeader, sortRows, useSort } from '../components/Grid'
import { RowMenu } from '../components/RowMenu'
import { RunVerdicts } from '../components/RunVerdicts'
import { DeskCell, DeskHeader, useDeskColumns, useDesks } from '../desks'

/**
 * The verdict report — TECHNICAL_DESIGN.md §6.4, proposal §6 second-release gate 3.
 * The Snapshot Evaluator pack.
 *
 * Every fund, counted and listed by where its latest run leaves it. **Not evaluable
 * is drawn as loudly as breach** (§6.4): a tile of the same weight, beside it, not
 * a muted afterthought below warn. **A fund nobody evaluated is listed**, because a
 * report that leaves it off reads, for that fund, as compliant.
 *
 * **A fund's verdicts open in place of the list**, as a snapshot does. Below a list
 * of many funds they were a scroll away from the row that opened them, and easily
 * never seen. *All funds* returns to the list as it was left, filters and order
 * included, and *Previous* and *Next* step through the funds listed, so the funds in
 * breach can be read one after another.
 *
 * **Every verdict says the day its holdings were as at**, and a fund says when that
 * day's answer has been overtaken: holdings loaded since, or its limits for the day
 * amended in the store since the run read them. Evaluating brings it up to date.
 *
 * Labelled advisory, with where pre-trade checks do run named beside it.
 */

const STANDINGS: Standing[] = ['breach', 'not_evaluable', 'warn', 'pass', 'not_run']

const LABEL: Record<Standing, string> = {
  breach: 'Breach',
  not_evaluable: 'Not evaluable',
  warn: 'Warn',
  pass: 'Pass',
  not_run: 'Not evaluated',
}

const COUNTED = ['breach', 'not_evaluable', 'warn', 'pass'] as const

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** The day the holdings were as at, as the file stated it, not moved into a time zone. */
function asAt(instant: string): string {
  return new Date(instant).toISOString().slice(0, 10)
}

/** A run's count of one verdict. Wording is counted on its own, as on a snapshot's runs. */
function counted(f: VerdictReportFund, state: (typeof COUNTED)[number]): number | null {
  if (!f.run) return null
  return state === 'not_evaluable' ? f.run.counts[state] - (f.run.text_only ?? 0) : f.run.counts[state]
}

/** Numbers as sortable text: a fund with no run sorts below a count of none. */
const padded = (n: number | null) => String((n ?? -1) + 1).padStart(8, '0')

const SORT_KEYS: Record<string, (f: VerdictReportFund) => string> = {
  fund: (f) => f.portfolio_code,
  standing: (f) => String(STANDINGS.indexOf(f.standing)),
  as_of: (f) => f.run?.snapshot_as_of ?? '',
  breach: (f) => padded(counted(f, 'breach')),
  not_evaluable: (f) => padded(counted(f, 'not_evaluable')),
  warn: (f) => padded(counted(f, 'warn')),
  pass: (f) => padded(counted(f, 'pass')),
  text_only: (f) => padded(f.run ? (f.run.text_only ?? 0) : null),
  ran: (f) => f.run?.ran_at ?? '',
}

/** What has overtaken a fund's run, in the words the list and the fund's view both use. */
function overtaken(f: VerdictReportFund): { text: string; testId: string }[] {
  return [
    ...(f.unevaluated_snapshot
      ? [{ text: `Holdings as at ${asAt(f.unevaluated_snapshot.as_of)} not yet evaluated`, testId: 'unevaluated' }]
      : []),
    ...(f.limits_changed ? [{ text: 'Limits changed since this run', testId: 'limits-changed' }] : []),
    ...(f.figures_changed ? [{ text: 'Benchmark figures loaded since this run', testId: 'figures-changed' }] : []),
  ]
}

function Advisory() {
  return (
    <>
      <span className="advisory" data-testid="advisory">
        Advisory
      </span>
      <Explain term="Advisory">
        {' '}
        Each verdict judges a fund’s holdings as at its snapshot’s date against the limits in
        force on that date. Pre-trade checks run in your order management system against the
        exported rules.
      </Explain>
    </>
  )
}

/** How often verdicts agree with compliance’s own answers, beside Advisory (proposal §6, gate 3). Absent until measured. */
function AgreementFigure({ figure }: { figure?: AgreementHeadline | null }) {
  if (!figure) return null
  return (
    <span
      className="agreement-figure"
      data-testid="agreement-figure"
      title={`Of the ${figure.compared} verdicts compliance reviewed, it agreed with ${figure.agreed} and overrode ${figure.overridden}. ${figure.awaiting} standing verdicts await review.`}
    >
      {agreementText(figure)}
    </span>
  )
}

export function Verdicts({
  focusRun,
  focusSnapshot,
  canReview = false,
}: {
  /** Compliance reviews each verdict (decision 53). */
  canReview?: boolean
  /** A run to open on arriving, as when Scheduled Jobs opens one monitoring made. */
  focusRun?: string
  /** Holdings whose verdicts to open on arriving: the newest run that judged them. */
  focusSnapshot?: string
} = {}) {
  /** `YYYY-MM-DD`, or empty for the verdicts that stand now. */
  const [on, setOn] = useState('')
  const [report, setReport] = useState<VerdictReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fund, setFund] = useState('')
  const [only, setOnly] = useState('')
  /** The desk the list is narrowed to, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const desks = useDesks()
  const deskColumns = useDeskColumns()
  // Most severe first until somebody sorts otherwise.
  const { sort, onSort } = useSort({ key: 'standing', direction: 'asc' })
  /** The fund whose verdicts are open in place of the list, and those verdicts once fetched. */
  const [openFund, setOpenFund] = useState<string | null>(null)
  const [open, setOpen] = useState<EvaluationDetail | null>(null)
  /** The fund being evaluated. */
  const [working, setWorking] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [reads, setReads] = useState(0)
  /** The fund to bring back into view on returning to the list. */
  const [returnTo, setReturnTo] = useState<string | null>(null)

  useEffect(() => {
    if (!focusRun) return
    let live = true
    setOpen(null)
    api
      .evaluation(focusRun)
      .then((found) => {
        if (!live) return
        setOpenFund(found.portfolio_id)
        setOpen(found)
      })
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [focusRun])

  // Holdings a scheduled load collected, opened on the newest run that judged them.
  useEffect(() => {
    if (!focusSnapshot) return
    let live = true
    setOpen(null)
    api
      .evaluations({ snapshotId: focusSnapshot })
      .then(async (runs) => {
        if (!live) return
        const newest = runs[0]
        if (!newest) {
          setError('No verdicts on these holdings yet. Choose Evaluate in the fund’s menu.')
          return
        }
        setOpenFund(newest.portfolio_id)
        const found = await api.evaluation(newest.id)
        if (live) setOpen(found)
      })
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [focusSnapshot])

  // The funds already listed stay while the next report is fetched, so choosing a
  // date does not empty the page under the control.
  useEffect(() => {
    let live = true
    setError(null)
    api
      .verdictReport({ on: on || undefined })
      .then((found) => live && setReport(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [on, reads])

  // Back on the list, at the fund that was open rather than at the top of a long list.
  useEffect(() => {
    if (openFund || !returnTo) return
    document.querySelector(`[data-fund="${returnTo}"]`)?.scrollIntoView?.({ block: 'center' })
    setReturnTo(null)
  }, [openFund, returnTo])

  const funds = report?.funds ?? []
  const shown = sortRows(
    funds.filter(
      (f) =>
        (!fund || f.portfolio_id === fund) &&
        (!only || f.standing === only) &&
        (!desk || desks.fund(f.portfolio_id) === desk),
    ),
    sort,
    { ...SORT_KEYS, desk: (f) => desks.fund(f.portfolio_id) ?? '' },
  )

  async function openVerdicts(f: VerdictReportFund, runId?: string) {
    const id = runId ?? f.run?.id
    if (!id) return
    setOpenFund(f.portfolio_id)
    setOpen(null)
    setError(null)
    document.querySelector('.verdict-report')?.scrollIntoView?.({ block: 'start' })
    try {
      setOpen(await api.evaluation(id))
    } catch (e) {
      setError(said(e))
    }
  }

  function backToList() {
    setReturnTo(openFund)
    setOpenFund(null)
    setOpen(null)
  }

  async function evaluate(f: VerdictReportFund) {
    const snapshotId = f.unevaluated_snapshot?.id ?? f.run?.snapshot_id
    if (!snapshotId) return
    setWorking(f.portfolio_id)
    setError(null)
    try {
      const run = await api.evaluate(snapshotId)
      setReads((n) => n + 1)
      await openVerdicts(f, run.id)
    } catch (e) {
      setError(said(e))
    } finally {
      setWorking(null)
    }
  }

  async function exportAs(format: 'csv' | 'xlsx') {
    setExporting(true)
    setError(null)
    try {
      await api.downloadVerdictReport({ on: on || undefined, portfolioId: fund || undefined }, format)
    } catch (e) {
      setError(said(e))
    } finally {
      setExporting(false)
    }
  }

  const errorLine = error && (
    <p className="error" role="alert">
      {error}
    </p>
  )

  // One fund's verdicts, in place of the list.
  const current = openFund ? funds.find((f) => f.portfolio_id === openFund) : undefined
  if (openFund) {
    // The funds listed that have verdicts to read, in the order the list shows them.
    const readable = shown.filter((f) => f.run)
    const at = readable.findIndex((f) => f.portfolio_id === openFund)
    const previous = at > 0 ? readable[at - 1] : undefined
    const next = at >= 0 ? readable[at + 1] : undefined
    const notes = current ? overtaken(current) : []
    return (
      <section className="verdict-report" data-testid="fund-verdicts">
        <header className="page-head">
          <div className="fund-view-bar">
            <button type="button" className="ghost" onClick={backToList}>
              ← All funds
            </button>
            {at >= 0 && (
              <nav className="fund-stepper" aria-label="Step through the funds listed">
                <button
                  type="button"
                  className="ghost"
                  disabled={!previous}
                  onClick={() => previous && void openVerdicts(previous)}
                >
                  ← {previous ? previous.portfolio_code : 'Previous'}
                </button>
                <span data-testid="fund-position">
                  {at + 1} of {readable.length}
                </span>
                <button
                  type="button"
                  className="ghost"
                  disabled={!next}
                  onClick={() => next && void openVerdicts(next)}
                >
                  {next ? next.portfolio_code : 'Next'} →
                </button>
              </nav>
            )}
          </div>
          <div className="report-title">
            <h2>{current?.portfolio_code ?? open?.portfolio_code}</h2>
            {current && <span className={`verdict-count ${current.standing}`}>{LABEL[current.standing]}</span>}
            <Advisory />
            <AgreementFigure figure={report?.agreement} />
          </div>
          {current && current.portfolio_name !== current.portfolio_code && (
            <p className="note">{current.portfolio_name}</p>
          )}
          {current && notes.length > 0 && (
            <p className="fund-overtaken">
              {notes.map((n) => (
                <span key={n.testId} className="check-differs" data-testid={n.testId}>
                  {n.text}
                </span>
              ))}
              <button
                type="button"
                className="ghost"
                disabled={working === current.portfolio_id}
                onClick={() => void evaluate(current)}
              >
                {current.unevaluated_snapshot
                  ? `Evaluate ${asAt(current.unevaluated_snapshot.as_of)}`
                  : 'Evaluate again'}
              </button>
            </p>
          )}
        </header>
        {errorLine}
        {open ? (
          <RunVerdicts key={open.id} detail={open} canReview={canReview} />
        ) : (
          !error && <p className="loading">Loading…</p>
        )}
      </section>
    )
  }

  return (
    <section className="verdict-report">
      <header className="page-head">
        <div className="report-title">
          <h2>Verdicts</h2>
          <Advisory />
          <AgreementFigure figure={report?.agreement} />
        </div>
        <p className="note">
          Where every fund stands on its latest evaluation, each on the holdings as at its
          snapshot’s date. Part of the <EvaluatorName strong={false} />.
          <AddOnFootnote />
        </p>
      </header>

      <div className="report-controls">
        <label>
          As at
          <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
        </label>
        {on && (
          <button type="button" className="link" onClick={() => setOn('')}>
            Latest
          </button>
        )}
        <span className="grow" />
        <RowMenu
          label="Export"
          text="Export"
          testId="export-menu"
          disabled={exporting || !report}
          items={[
            { label: 'CSV', onSelect: () => void exportAs('csv') },
            { label: 'Excel', onSelect: () => void exportAs('xlsx') },
          ]}
        />
      </div>

      {errorLine}

      {report === null ? (
        <p className="loading">Loading…</p>
      ) : funds.length === 0 ? (
        <p className="empty" data-testid="report-empty">
          No funds set up yet. Add them under Funds & groups.
        </p>
      ) : (
        <>
          <div className="verdict-tiles" data-testid="verdict-totals">
            {STANDINGS.map((s) => (
              <button
                key={s}
                type="button"
                className={`verdict-tile ${s}${only === s ? ' on' : ''}`}
                aria-pressed={only === s}
                aria-label={`${report.totals[s]} ${report.totals[s] === 1 ? 'fund' : 'funds'}: ${LABEL[s]}`}
                data-testid={`total-${s}`}
                onClick={() => setOnly(only === s ? '' : s)}
              >
                <strong>{report.totals[s]}</strong>
                <span>{LABEL[s]}</span>
              </button>
            ))}
          </div>

          <div className="table-scroll">
            <Showing
              shown={shown.length}
              total={funds.length}
              noun="funds"
              onClear={() => {
                setFund('')
                setOnly('')
                setDesk('')
              }}
            />
            <table className="grid stacked" data-testid="report">
              <thead>
                <tr>
                  <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
                  <FilterHeader
                    label="Fund"
                    sortKey="fund"
                    sort={sort}
                    onSort={onSort}
                    value={fund}
                    onChange={setFund}
                    all="Every fund"
                    testId="filter-fund"
                    options={funds.map((f) => ({ value: f.portfolio_id, label: f.portfolio_code }))}
                  />
                  <FilterHeader
                    label="Standing"
                    sortKey="standing"
                    sort={sort}
                    onSort={onSort}
                    value={only}
                    onChange={setOnly}
                    all="Every standing"
                    testId="filter-standing"
                    options={STANDINGS.map((s) => ({ value: s, label: LABEL[s] }))}
                  />
                  <SortHeader label="Holdings as at" sortKey="as_of" sort={sort} onSort={onSort} />
                  <SortHeader label="Breach" sortKey="breach" sort={sort} onSort={onSort} className="num" />
                  <SortHeader label="Not evaluable" sortKey="not_evaluable" sort={sort} onSort={onSort} className="num" />
                  <SortHeader label="Warn" sortKey="warn" sort={sort} onSort={onSort} className="num" />
                  <SortHeader label="Pass" sortKey="pass" sort={sort} onSort={onSort} className="num" />
                  <SortHeader label="Text only" sortKey="text_only" sort={sort} onSort={onSort} className="num" />
                  <SortHeader label="Ran" sortKey="ran" sort={sort} onSort={onSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={10 + deskColumns} className="empty">
                      No fund matches these filters.
                    </td>
                  </tr>
                )}
                {shown.map((f) => {
                  const run = f.run
                  const evaluable = f.unevaluated_snapshot ?? run
                  return (
                    <tr
                      key={f.portfolio_id}
                      data-testid="report-fund"
                      data-fund={f.portfolio_id}
                      className={run ? 'clickable' : ''}
                      title={run ? `Open ${f.portfolio_code}’s verdicts` : undefined}
                      onClick={() => void openVerdicts(f)}
                    >
                      <DeskCell fund={f.portfolio_id} />
                      <td>
                        {run ? (
                          <button
                            type="button"
                            className="link-button"
                            onClick={(e) => {
                              e.stopPropagation()
                              void openVerdicts(f)
                            }}
                          >
                            {f.portfolio_code}
                          </button>
                        ) : (
                          <strong>{f.portfolio_code}</strong>
                        )}
                        {f.portfolio_name !== f.portfolio_code && <span className="clause">{f.portfolio_name}</span>}
                      </td>
                      <td>
                        <span className={`verdict-count ${f.standing}`}>{LABEL[f.standing]}</span>
                      </td>
                      <td>
                        {run ? asAt(run.snapshot_as_of) : '—'}
                        {overtaken(f).map((n) => (
                          <span key={n.testId} className="clause check-differs" data-testid={n.testId}>
                            {n.text}
                          </span>
                        ))}
                        {!run && !f.unevaluated_snapshot && <span className="clause">No holdings loaded</span>}
                      </td>
                      {COUNTED.map((state) => {
                        const n = counted(f, state)
                        return (
                          <td key={state} className="num">
                            {n !== null && <span className={`verdict-count ${state}${n === 0 ? ' zero' : ''}`}>{n}</span>}
                          </td>
                        )
                      })}
                      <td className="num">
                        {run && <span className="verdict-count text-only">{run.text_only ?? 0}</span>}
                      </td>
                      <td>
                        {run && new Date(run.ran_at).toLocaleString()}
                        {run?.run_by && <span className="clause">{run.run_by}</span>}
                      </td>
                      {/* The menu's own clicks stay in the menu; the row opens the fund. */}
                      <td className="run-actions" onClick={(e) => e.stopPropagation()}>
                        <RowMenu
                          label={`Actions for ${f.portfolio_code}`}
                          testId="fund-menu"
                          items={[
                            {
                              label: 'Verdicts',
                              disabled: !run,
                              why: run ? undefined : 'Not evaluated yet.',
                              onSelect: () => void openVerdicts(f),
                            },
                            {
                              label: f.unevaluated_snapshot
                                ? `Evaluate ${asAt(f.unevaluated_snapshot.as_of)}`
                                : 'Evaluate again',
                              disabled: !evaluable || working === f.portfolio_id,
                              why: evaluable ? undefined : 'Load a snapshot of this fund first.',
                              onSelect: () => void evaluate(f),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
